import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const GATEWAYS = [
  { value: '', label: '— Select Payment Gateway —' },
  { value: 'razorpay', label: 'Razorpay', logo: '💳', desc: 'India\'s leading payment gateway — supports UPI, Cards, NetBanking, Wallets', color: 'bg-blue-50 border-blue-200 text-blue-800' },
  { value: 'payu', label: 'PayU', logo: '💰', desc: 'Trusted by 450,000+ businesses across India', color: 'bg-orange-50 border-orange-200 text-orange-800' },
  { value: 'stripe', label: 'Stripe', logo: '⚡', desc: 'Global payment platform — 135+ currencies, 40+ countries', color: 'bg-violet-50 border-violet-200 text-violet-800' },
  { value: 'paytm', label: 'PayTM', logo: '📱', desc: 'India\'s largest digital payments ecosystem', color: 'bg-sky-50 border-sky-200 text-sky-800' },
];

const GATEWAY_FIELDS = {
  razorpay: [
    { key: 'paymentKeyId', label: 'Key ID', placeholder: 'rzp_test_XXXXXXXXXXXX', secret: false },
    { key: 'paymentKeySecret', label: 'Key Secret', placeholder: 'Enter Razorpay Key Secret', secret: true },
    { key: 'paymentWebhookSecret', label: 'Webhook Secret', placeholder: 'Optional — enter webhook secret', secret: true },
  ],
  payu: [
    { key: 'paymentKeyId', label: 'Merchant Key', placeholder: 'Enter PayU Merchant Key', secret: false },
    { key: 'paymentKeySecret', label: 'Salt', placeholder: 'Enter PayU Salt', secret: true },
    { key: 'paymentMerchantId', label: 'Merchant ID', placeholder: 'Enter PayU Merchant ID', secret: false },
  ],
  stripe: [
    { key: 'paymentKeyId', label: 'Publishable Key', placeholder: 'pk_test_XXXXXXXXXXXX', secret: false },
    { key: 'paymentKeySecret', label: 'Secret Key', placeholder: 'sk_test_XXXXXXXXXXXX', secret: true },
    { key: 'paymentWebhookSecret', label: 'Webhook Signing Secret', placeholder: 'whsec_XXXXXXXXXXXX', secret: true },
  ],
  paytm: [
    { key: 'paymentMerchantId', label: 'Merchant ID (MID)', placeholder: 'Enter PayTM MID', secret: false },
    { key: 'paymentKeySecret', label: 'Merchant Key', placeholder: 'Enter PayTM Merchant Key', secret: true },
  ],
};

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const PaymentConfig = () => {
  const [form, setForm] = useState({ paymentGateway: '', paymentKeyId: '', paymentKeySecret: '', paymentMode: 'test', paymentMerchantId: '', paymentWebhookSecret: '' });
  const [configured, setConfigured] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  useEffect(() => {
    axios.get(`${API_BASE}/settings/payment-config`, { headers: headers() })
      .then(res => { setForm(prev => ({ ...prev, ...res.data })); setConfigured(res.data.configured); })
      .catch(() => flash('error', 'Failed to load payment configuration'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.paymentGateway) return flash('error', 'Please select a payment gateway');
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/settings/payment-config`, form, { headers: headers() });
      setConfigured(true);
      flash('success', 'Payment configuration saved successfully');
    } catch (err) {
      flash('error', err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const gwInfo = GATEWAYS.find(g => g.value === form.paymentGateway);
  const fields = GATEWAY_FIELDS[form.paymentGateway] || [];

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Payment Integration</h1>
              <p className="text-xs text-gray-400 mt-0.5">Configure your payment gateway for fee collection</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {msg.text && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}

          {/* Status Card */}
          <div className="bg-white rounded-lg shadow p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${configured ? 'bg-green-100' : 'bg-gray-100'}`}>
              {gwInfo?.logo || '💳'}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-semibold text-gray-800">Payment Gateway Status</h2>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {configured ? '✓ Configured' : 'Not configured'}
                </span>
                {configured && (
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${form.paymentMode === 'live' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {form.paymentMode === 'live' ? '🔴 Live Mode' : '🟡 Test Mode'}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {configured ? `Active gateway: ${gwInfo?.label || form.paymentGateway}` : 'No payment gateway configured'}
              </p>
            </div>
          </div>

          {/* Gateway Selector */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Select Payment Gateway</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {GATEWAYS.filter(g => g.value).map(gw => (
                <button
                  key={gw.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, paymentGateway: gw.value }))}
                  className={`p-4 rounded-xl border-2 text-left transition ${form.paymentGateway === gw.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'}`}
                >
                  <div className="text-2xl mb-2">{gw.logo}</div>
                  <div className="font-semibold text-gray-800 text-sm">{gw.label}</div>
                  <div className="text-xs text-gray-400 mt-1 leading-tight">{gw.desc}</div>
                </button>
              ))}
            </div>

            {form.paymentGateway && (
              <form onSubmit={handleSave} className="space-y-5">
                {/* Mode Toggle */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-3">Integration Mode</p>
                  <div className="flex gap-3">
                    {['test', 'live'].map(mode => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, paymentMode: mode }))}
                        className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-semibold transition ${form.paymentMode === mode
                          ? mode === 'live' ? 'border-red-500 bg-red-50 text-red-700' : 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        {mode === 'live' ? '🔴 Live Mode' : '🟡 Test / Sandbox Mode'}
                      </button>
                    ))}
                  </div>
                  {form.paymentMode === 'live' && (
                    <p className="text-xs text-red-600 mt-2 font-medium">⚠ Live mode processes real payments. Use only with production credentials.</p>
                  )}
                </div>

                {/* Dynamic Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {fields.map(({ key, label, placeholder, secret }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <div className="relative">
                        <input
                          type={secret && !showSecrets[key] ? 'password' : 'text'}
                          value={form[key] || ''}
                          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="w-full border rounded-lg px-3 py-2 pr-14 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                        />
                        {secret && (
                          <button type="button" onClick={() => setShowSecrets(s => ({ ...s, [key]: !s[key] }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">
                            {showSecrets[key] ? 'Hide' : 'Show'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Webhook Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Webhook Endpoint</p>
                  <code className="text-xs text-blue-700 font-mono break-all">
                    {window.location.origin.replace(':3000', ':5001')}/api/webhooks/{form.paymentGateway}
                  </code>
                  <p className="text-xs text-blue-600 mt-1">Add this URL to your {gwInfo?.label} dashboard webhook settings.</p>
                </div>

                <div className="pt-2">
                  <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                    {saving ? 'Saving...' : 'Save Payment Configuration'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfig;
